import React, { PropsWithChildren, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { collectBlockedAreas } from '@components/security/blockedAreas.ts';
import { useSecurityMode } from '@components/security/SecurityModeContext';
import { useSecuredFlag } from '@components/security/useSecuredFlag.ts';
import { setPanelMinimized } from '@components/panelDock/panelDockStore.ts';
import AssistantSidebar from './AssistantSidebar.tsx';
import { ASSISTANT_PANEL_KEY, AssistantContext, AssistantContextValue } from './assistantContext.ts';
import { clearPageContext, setBlockedAreasSource, setPageContextEnabled } from './pageContext/pageContextStore.ts';

/**
 * Owns the assistant panel and its open state, app-wide.
 *
 * It has to live above the router: ResponsiveLayout is rendered by each page, so it remounts on every
 * navigation — which used to close the panel and drop the conversation the moment the user followed a
 * step the assistant had just described. Mounted here, the panel (and the chat state inside it)
 * survives navigation, which is the whole point of an assistant that walks you through the app.
 *
 * The permission gate is declared once here and handed out through the context, so the menu entry,
 * the mobile bar and the panel itself cannot drift apart.
 */
const AssistantProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const flag = useSecuredFlag('menu-ai-assistant', {
    kind: 'visibility',
    description: 'AI assistant',
    permissions: [PermissionTypesEnum.PERMISSION_AI_ASSISTANT],
  });
  const [isOpen, setIsOpen] = useState(false);
  const granted = flag.granted;
  const { registry, setCollecting } = useSecurityMode();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const [searchParams] = useSearchParams();

  // Page context (what the user sees, attached to each question) is recorded only for users who have
  // the assistant. A layout effect, so it is on before the first page's messages register in their
  // (passive) effects. `collecting` fills the security registry without showing security mode.
  useLayoutEffect(() => {
    setPageContextEnabled(granted);
    setCollecting(granted);
    return () => {
      setPageContextEnabled(false);
      setCollecting(false);
    };
  }, [granted, setCollecting]);

  useEffect(() => {
    if (!granted || !registry) return;
    setBlockedAreasSource(() => collectBlockedAreas(registry.getAreas()));
    return () => setBlockedAreasSource(null);
  }, [granted, registry]);

  // Nothing recorded for one user or company may be sent with another's question. Logout reloads the
  // page, which empties the store anyway; a switch without a reload does not.
  const identity = `${loggedUser?.id ?? ''}|${loggedUser?.companyId ?? ''}|${searchParams.get('runAs') ?? ''}`;
  const identityRef = useRef(identity);
  useEffect(() => {
    if (identityRef.current === identity) return;
    identityRef.current = identity;
    clearPageContext();
  }, [identity]);

  // Also restores a minimized panel: the menu entry is the obvious way back, and without this it would
  // do nothing while the panel sits minimized (it is already open).
  const open = useCallback(() => {
    setIsOpen(true);
    setPanelMinimized(ASSISTANT_PANEL_KEY, false);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // Deliberately keyed on what the flag depends on, not the flag: useSecuredFlag hands back a fresh
  // object — and, while a security registry exists, fresh metadata — on every render, so depending on
  // either would rebuild the context value each time and re-render everything that reads it. The
  // metadata only changes with the permission or when the registry comes and goes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo<AssistantContextValue>(() => ({ flag, isOpen, open, close }), [granted, registry, isOpen, open, close]);

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {/* Mounted for the whole session, not per page. The panel renders nothing until it is opened,
          and closing it keeps the conversation in memory. */}
      {flag.granted && <AssistantSidebar visible={isOpen} onHide={close} />}
    </AssistantContext.Provider>
  );
};

export default AssistantProvider;
